import Eventbus from '@libs/eventbus'
import { Camp } from '@libs/types/Camp.ts'
import { Box, Grid, Typography } from '@mui/material'
import { setCurrentPage } from '@store/campSearch/campSearchSlice'
import { AddToListDrawer } from '@stories/Components/AddtoList/AddToListDrawer.tsx'
import mixpanel from 'mixpanel-browser'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'

import boostedValues from '../../camp_boost_order.json'
import CampListSortButton from '../../src/components/CampList/CampListSortButton.tsx'
import { CampListItemSkeleton } from '../components/CampList/CampListItem.skeleton.tsx'
import CampListSearchBar from '../components/CampList/CampListSearchBar.tsx'
import Paginate from '../components/Paginate/index.tsx'
import { useAppDispatch, useAppSelector } from '../store/hooks.ts'
import { CampGridItem } from '../stories/Components/CampListItem/CampGridItem.tsx'

const List: React.FC = () => {
  const dispatch = useAppDispatch()

  const { campListFiltered, isLoading, isFilteringLoading } = useAppSelector((state) => state.campSearch)
  const selectedMetro = useAppSelector((state) => state.metro.selectedMetro)

  const pagination = useAppSelector((state) => state.campSearch.pagination)
  const sortBy = useAppSelector((state) => state.campFilters.sort.by)

  const [favCamId, setFavCampId] = useState<number>(0)

  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page))
  }

  useEffect(() => {
    mixpanel.track_pageview({ page: 'Camps' })
    Eventbus.on(Eventbus.RESET_PAGING, () => {
      dispatch(setCurrentPage(1))
    })
  }, [])

  // Calculate the boosted camps to show based on the current page
  const BOOSTED_CAMPS_PER_PAGE = 4
  const startIndex = (pagination.currentPage - 1) * BOOSTED_CAMPS_PER_PAGE
  const endIndex = startIndex + BOOSTED_CAMPS_PER_PAGE

  const boostedCamps = boostedValues
    .sort((a, b) => b.boost_value - a.boost_value)
    .map((boost) => {
      const camp = campListFiltered.find((camp) => camp.id === boost.camp_id)
      return camp ? camp : null // Merge only if camp exists
    })
    .filter((camp) => camp !== null)

  const numOfPagesWithBoostedCamps = Math.ceil(boostedCamps.length / BOOSTED_CAMPS_PER_PAGE)

  const boostedCampsForCurrentPage = boostedCamps.slice(startIndex, endIndex)

  // Check if there are less than 4 boosted camps and add additional Camperoni boosted camps
  let additionalCamperoniBoostedCamps: Camp[] = []
  if (boostedCampsForCurrentPage.length > 0 && boostedCampsForCurrentPage.length < BOOSTED_CAMPS_PER_PAGE) {
    const remainingSlots = BOOSTED_CAMPS_PER_PAGE - boostedCampsForCurrentPage.length
    additionalCamperoniBoostedCamps = campListFiltered.slice(-remainingSlots).reverse()
  }

  // Calculate how many boosted camps to show on the current page (both regular and additional)
  const boostedCampsOnCurrentPage =
    sortBy === 'featured' &&
    campListFiltered.length >= (pagination.currentPage - 1) * 20 + 15 &&
    boostedCampsForCurrentPage.length > 0
      ? boostedCampsForCurrentPage.length + additionalCamperoniBoostedCamps.length
      : 0

  // Get the total count of boosted camps shown on previous pages
  const totalBoostedCampsInPreviousPages =
    Math.min(pagination.currentPage - 1, numOfPagesWithBoostedCamps) * BOOSTED_CAMPS_PER_PAGE

  // Calculate indices for boosted and organic camps on the current page
  const boostedCampStartIndex = (pagination.currentPage - 1) * 20 + totalBoostedCampsInPreviousPages + 1

  const organicCampStartIndex = boostedCampStartIndex + boostedCampsOnCurrentPage

  return (
    <>
      <Helmet>
        <title>Camperoni Website - Camp search</title>
        <link rel="canonical" href={`https://www.camperoni.com/camps`} />
      </Helmet>
      <Box sx={{ flexGrow: 1 }} px={2}>
        <CampListSearchBar />
        <Box pt={3} pb={4.5}>
          <Typography component="h1" variant="h4" data-testid="search_heading">
            {`${selectedMetro ? selectedMetro.name : ''} Camp Search`}
          </Typography>
        </Box>
        <Box mb={3}>
          <CampListSortButton />
        </Box>
        {isLoading || isFilteringLoading ? (
          <Grid container columnSpacing={{ xs: 1, sm: 4 }} rowSpacing={{ xs: 2, sm: 4 }}>
            {Array.from(new Array(12)).map((_, index) => (
              <Grid item xs={12} sm={6} md={6} lg={3} key={index}>
                <CampListItemSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            {boostedCampsOnCurrentPage > 0 && (
              <Grid container columnSpacing={{ xs: 1, sm: 4 }}>
                {[...boostedCampsForCurrentPage, ...additionalCamperoniBoostedCamps].map((camp, index: number) => (
                  <CampGridItem
                    key={index}
                    item={camp}
                    setFavCampId={setFavCampId}
                    isBoosted={true}
                    campIndex={boostedCampStartIndex + index}
                    campPagePosition={index + 1}
                    pageIndex={pagination.currentPage}
                  />
                ))}
              </Grid>
            )}

            {campListFiltered && campListFiltered.length > 0 ? (
              <Grid container columnSpacing={{ xs: 1, sm: 4 }} data-testid="camp-items-container">
                <Paginate
                  itemsPerPage={20}
                  data={campListFiltered}
                  renderItem={(item: Camp, index: number) => (
                    <CampGridItem
                      item={item}
                      setFavCampId={setFavCampId}
                      campIndex={organicCampStartIndex + index}
                      campPagePosition={index + (boostedCampsOnCurrentPage + 1)}
                      pageIndex={pagination.currentPage}
                    />
                  )}
                  currentPage={pagination.currentPage}
                  onPageChange={handlePageChange}
                />
              </Grid>
            ) : (
              <Typography variant="h6">No camps found. Try clearing your filters.</Typography>
            )}
          </>
        )}
      </Box>
      <AddToListDrawer campId={favCamId} onClose={() => setFavCampId(0)} />
    </>
  )
}

export default List
